import { useState } from "react";
import { useSearchParams } from "react-router";
import { ApiError, api, fetchClient } from "@/api/client";
import { AppearanceToggle } from "@/app/AppearanceToggle";
import rainScalesLogo from "@/assets/rainscales-logo.png";
import { CeliaBrand } from "@/components/CeliaBrand";
import { btn } from "@/components/actions";
import { LoginVisionBackdrop } from "./LoginVisionBackdrop";
import "./login.css";

const SIGN_IN_UNAVAILABLE = "The sign-in service could not be reached. Please try again.";
const SIGN_IN_FAILED = "Sign in failed. Please try again.";

function signInFailure(error: unknown): string {
  if ((error instanceof ApiError && error.status >= 500) || error instanceof TypeError ||
      (error instanceof DOMException && ["AbortError", "TimeoutError", "NetworkError"].includes(error.name))) {
    return SIGN_IN_UNAVAILABLE;
  }
  if (error instanceof ApiError && error.status === 401 && error.code === "bad_credentials") {
    return "Sign in failed. Check your credentials and try again.";
  }
  return SIGN_IN_FAILED;
}

function returnPath(value: string | null): string {
  if (!value?.startsWith("/") || value.startsWith("//") || /[\\\u0000-\u0020\u007f]/.test(value)) return "/";
  try {
    const destination = new URL(value, window.location.origin);
    return destination.origin === window.location.origin ? destination.pathname + destination.search + destination.hash : "/";
  } catch {
    return "/";
  }
}

export function LoginPage() {
  const [params] = useSearchParams();
  const back = returnPath(params.get("return"));
  const reauth = params.get("reauth") === "1" || params.get("reauth") === "true";
  const providers = api.useQuery("get", "/api/auth/providers");
  const [failure, setFailure] = useState<string | null>(params.has("error")
    ? params.get("error") === "sso_unavailable" ? SIGN_IN_UNAVAILABLE : SIGN_IN_FAILED
    : null);
  const [submitting, setSubmitting] = useState(false);

  return (
    <div className="login-page">
      <header className="login-header">
        <AppearanceToggle />
      </header>

      <main className="login-main">
        <LoginVisionBackdrop />
        <section className="login-card" aria-labelledby="login-heading">
          <div className="login-intro">
            <div className="login-intro-brand"><CeliaBrand markSize={30} /></div>
            <h1 id="login-heading">Sign in</h1>
          </div>

          {failure && (
            <div className="login-error text-danger" role="alert">
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path strokeLinecap="round" d="M12 7v6m0 4h.01" /></svg>
              <span>{failure}</span>
            </div>
          )}

          {providers.isPending && <p className="login-provider-message" role="status">Loading sign-in options…</p>}
          {providers.isError && <p className="login-provider-message text-danger" role="alert">Sign-in options are unavailable. Please try again.</p>}

          {providers.data?.keycloak && (
            <a className={`${btn("accent", "form")} login-sso`} href={`/api/auth/keycloak?back=${encodeURIComponent(back)}${reauth ? "&reauth=1" : ""}`}>
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="7.5" cy="15.5" r="4.5" /><path d="m11 12 9-9m-4 0 4 4m-7-1 4 4" /></svg>
              Continue with Keycloak SSO
            </a>
          )}

          {providers.data?.keycloak && providers.data?.local && <div className="login-divider"><span>or sign in with your account</span></div>}

          {providers.data?.local && (
            <form className="login-form" onSubmit={async (event) => {
              event.preventDefault();
              setSubmitting(true);
              setFailure(null);
              const form = new FormData(event.currentTarget);
              try {
                const result = await fetchClient.POST("/api/auth/login", {
                  body: { username: String(form.get("username")), password: String(form.get("password")) },
                });
                if (result.error) throw result.error;
                location.assign(back);
              } catch (error) {
                setFailure(signInFailure(error));
              } finally {
                setSubmitting(false);
              }
            }}>
              <label htmlFor="login-username">Username</label>
              <input id="login-username" name="username" type="text" autoComplete="username" placeholder="Enter username" required disabled={submitting} />
              <label htmlFor="login-password">Password</label>
              <input id="login-password" name="password" type="password" autoComplete="current-password" placeholder="Enter password" required disabled={submitting} />
              <button type="submit" className={`${btn(providers.data?.keycloak ? "plain" : "accent", "form")} login-submit`} disabled={submitting}>
                {submitting ? "Signing in…" : "Sign in"}
                {!submitting && <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14m-7-7 7 7-7 7" /></svg>}
              </button>
            </form>
          )}
          {providers.data && !providers.data.keycloak && !providers.data.local && <p className="login-provider-message" role="status">No sign-in method is available.</p>}
        </section>
      </main>

      <footer className="login-footer">
        <div className="login-footer-brand">
          <span>A product by</span>
          <img src={rainScalesLogo} alt="RainScales" />
        </div>
        <span className="login-footer-copyright">© {new Date().getFullYear()} RainScales</span>
      </footer>
    </div>
  );
}
